import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../Layout';
import { useAuth } from '../../auth/AuthContext';

const API = 'https://api.kallabakari.is';

const STATUS_MAP = {
  pending:   { label: 'Í bið',    cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  confirmed: { label: 'Staðfest', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { label: 'Lokið',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Afbókað', cls: 'bg-red-50 text-red-600 border-red-200' },
};

function formatProductLine(p) {
  switch (p.type) {
    case 'cake':     return `${p.details.cake}${p.details.size ? ` (${p.details.size})` : ''}`;
    case 'bread':    return `${p.details.bread} × ${p.details.quantity}`;
    case 'minidonut': return `Minidonuts × ${p.details.quantity}`;
    case 'bakkelsi': return `${p.details.name} × ${p.details.quantity}`;
    case 'salat':    return `${p.details.name} × ${p.details.quantity}`;
    case 'supa':     return `${p.details.name} × ${p.details.quantity}`;
    default:         return `${p.details.name || p.details.other || 'Vara'} × ${p.details.quantity ?? 1}`;
  }
}

const formatISK = n =>
  new Intl.NumberFormat('is-IS', { style: 'currency', currency: 'ISK' }).format(n);

const formatDate = iso =>
  new Date(iso).toLocaleDateString('is-IS', { day: 'numeric', month: 'long', year: 'numeric' });

export default function Orders() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/company/orders`, { headers: { Authorization: token } })
      .then(res => {
        const sorted = [...res.data].sort((a, b) => new Date(b.date) - new Date(a.date));
        setOrders(sorted);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const toggleExpand = (id) => setExpanded(prev => (prev === id ? null : id));

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Pantanir</h1>
        <button
          onClick={() => navigate('/order/new')}
          className="bg-stone-500 hover:bg-stone-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          + Ný pöntun
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Hleður pantanir...</div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center">
          <p className="text-gray-400 text-sm">Engar pantanir skráðar.</p>
          <button
            onClick={() => navigate('/order/new')}
            className="mt-4 text-stone-600 hover:text-stone-800 text-sm font-medium transition-colors"
          >
            Búa til fyrstu pöntunina →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {orders.map((order, idx) => {
            const status = STATUS_MAP[order.status] || { label: order.status, cls: 'bg-gray-50 text-gray-500 border-gray-200' };
            const isExpanded = expanded === order._id;
            const isLast = idx === orders.length - 1;

            return (
              <div key={order._id} className={!isLast ? 'border-b border-gray-100' : ''}>
                {/* Row */}
                <button
                  type="button"
                  onClick={() => toggleExpand(order._id)}
                  className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm font-medium text-gray-800">{formatDate(order.date)}</span>
                      {order.pickupTime && (
                        <span className="text-xs text-stone-600 font-medium">
                          {order.deliveryType === 'delivery' ? 'Afhent' : 'Sótt'} kl. {order.pickupTime}
                        </span>
                      )}
                      <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full border ${status.cls}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {order.products.map(formatProductLine).join(' · ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {order.totalAmount > 0 && (
                      <span className="text-sm font-semibold text-gray-700">{formatISK(order.totalAmount)}</span>
                    )}
                    <span className={`text-gray-400 text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                  </div>
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-5 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="pt-3 space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Vörur</p>
                        <ul className="space-y-1">
                          {order.products.map((p, i) => (
                            <li key={i} className="text-sm text-gray-700">{formatProductLine(p)}</li>
                          ))}
                        </ul>
                      </div>

                      {order.note && (
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Athugasemd</p>
                          <p className="text-sm text-gray-600 italic">"{order.note}"</p>
                        </div>
                      )}

                      <div className="flex gap-3 pt-1">
                        <button
                          onClick={() => navigate(`/order/${order._id}`)}
                          className="text-xs font-medium text-stone-700 hover:text-stone-900 transition-colors"
                        >
                          Skoða / breyta →
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
